import { api } from '../../services/apiClient';

/** Etiquetas legibles de los tipos de pago reales en la BD (7 tipos). */
export const INCOME_TYPE_LABEL: Record<string, string> = {
  membership: 'Mensualidades',
  product: 'Vitrina',
  other: 'Otros pagos',
  license: 'Licencias',
  event: 'Eventos',
  voucher_trial: 'Clases de prueba',
  voucher_voucher: 'Vouchers',
  class: 'Clases sueltas',
};
export const INCOME_METHOD_LABEL: Record<string, string> = {
  cash: 'Efectivo', creditcard: 'Tarjeta', card: 'Tarjeta',
  wire: 'Transferencia', voucher: 'Voucher', other: 'Otro',
};

export interface MonthBucket {
  month: number; // 1..12
  total: number;
  n: number;
  byType: Record<string, number>;
  byMethod: Record<string, number>;
}
export interface IncomeReport {
  year: number;
  prevYear: number;
  years: number[];
  total: number;
  count: number;
  prevTotal: number;
  prevCount: number;
  byMonth: MonthBucket[];
  byType: { type: string; total: string; n: number }[];
  byMethod: { method: string; total: string; n: number }[];
}

export function getIncomeReport(year?: number) {
  return api.get<IncomeReport>(`/finances/report/income${year ? `?year=${year}` : ''}`);
}

// ── R2: alumnos inactivos (sin pago en N días) ──
export interface InactiveRow {
  account_id: number;
  firstname: string; lastname: string;
  phone_prefix: string | null; phone_number: string | null; email: string | null;
  has_photo: number;
  membership_name: string | null; membership_color: string | null;
  last_payment: string | null;
  days_since: number | null;
  payments_count: number;
}
export interface InactiveResp {
  days: number;
  data: InactiveRow[];
  summary: { inactive: number; never: number; with_old_payment: number; active_students: number };
}
export function getInactive(days = 60) {
  return api.get<InactiveResp>(`/members/inactive?days=${days}`);
}

// ── R3: ocupación de clases (inscritos vs capacidad) ──
export interface OccupancyRow {
  class_id: number; name: string; color: string | null;
  capacity: number; enrolled: number; pct: number;
  style: string | null; teacher: string | null;
}
export interface OccupancyResp {
  data: OccupancyRow[];
  byStyle: { style: string; classes: number; enrolled: number; capacity: number; pct: number }[];
  summary: {
    operating_classes: number; empty_classes: number;
    total_enrolled: number; total_capacity: number; occupancy_pct: number;
    full: number; near_empty: number;
  };
}
export function getOccupancy() {
  return api.get<OccupancyResp>('/classes/report/occupancy');
}

// ── Bajas / Lost Students (ex-alumnos, active=0) ──
export interface LostRow {
  account_id: number;
  firstname: string; lastname: string;
  phone_prefix: string | null; phone_number: string | null; email: string | null;
  has_photo: number;
  last_attendance: string | null;
  last_class_name: string | null;
  days_since: number | null;
  ltv: number;
  payments_count: number;
}
export interface LostResp {
  days: number | 'all';
  data: LostRow[];
  summary: { lost: number; with_phone: number; total_value: number; recent: number };
}
export function getLost(days: string = 'all') {
  return api.get<LostResp>(`/members/lost?days=${days}`);
}

// Evolución de clases (S7 / Class Students)
export interface EvolutionPayments { mes: number; bimestre: number; trimestre: number; otro: number; }
export interface EvolutionRow {
  class_id: number; name: string; color: string | null; style: string | null; teacher: string | null;
  enrolled: number; nuevos: number; asistencias: number; pagos: EvolutionPayments;
}
export interface EvolutionResp {
  month: string;
  data: EvolutionRow[];
  totals: { enrolled: number; nuevos: number; asistencias: number } & EvolutionPayments;
  months: string[];
}
export function getEvolution(month: string) {
  return api.get<EvolutionResp>(`/classes/report/evolution?month=${month}`);
}

// ── Conciliación del mes (B0, 14-oct-2026): pestaña dentro de Pronóstico y presupuesto ──
// Lo arma el servidor en lib/letsmoveConciliacion.js sobre las mismas libs del presupuesto (una sola fuente).
export interface ConcPersonaSemana {
  user_id: number; nombre: string; pay_mode: string; calculado: number; registrado: number; extras: number;
  pendiente: number; pagado: boolean; sin_tarifa: boolean; horas: number | null;
  ajustes?: number;   // B4: ajustes de la semana ya sumados a `calculado` (no son diferencia)
  // B4 (19-oct-2026): estado de pago explícito (registrada = pagada) y lo que falta pagar si está pendiente.
  estado?: 'pagada' | 'pendiente' | 'comision' | 'sin_monto'; porPagar?: number;
}
/** B4 (19-oct-2026): nómina pendiente de pago del mes (personas-semana sin «Registrar pago»). */
export interface NominaPendiente {
  total: number; vencida: number; enCurso: number; comision: number; pagadas: number; pendientes: number; hoy: string; explica: string;
  porSemana: { from: string; label: string; last: string; vencida: boolean; total: number; personas: { user_id: number; nombre: string; monto: number }[] }[];
}
export interface ConcSemana {
  from: string; to: string; last: string; label: string;
  calculada: number; registrada: number; extras: number; ajustes?: number; pendiente: number; personas: ConcPersonaSemana[];
}
export interface ConcProfe {
  user_id: number; nombre: string; modo: string; pay_mode: string; horas: number | null; base: number | null;
  sin_tarifa: boolean; sin_lista: number; no_dadas: number; extras: number; ajustes?: number;
  calculado: number; registrado: number; registros: number; diferencia: number;
  semanas: { from: string; calculado: number; registrado: number; extras: number; pendiente: number; run_ids: number[] }[];
}
export interface ConcComision { user_id: number; nombre: string; pct: number | null; base: number; calculada: number; entregada: number; entregas: number; pendiente: number }
export interface ConcAlumna {
  contact_id: number; nombre: string; plan: string | null; precio: number; inscritas: number; clases: string; ultimaMensualidad: string | null; esperadas?: number;
  // B4: «Con precio especial» (corrección en plan_overrides hacia su plan actual)
  nota?: string | null; marcadoAqui?: boolean; fecha?: string | null; por?: string | null;
}
export interface Conciliacion {
  mes: string; titulo: string; parcial: string | null; regla: { clave: string; texto: string };
  nomina: {
    porProfe: ConcProfe[]; calculada: number; registrada: number; sinTarifa: number; nota: string;
    semanas: ConcSemana[]; comisiones: ConcComision[]; rango: { from: string; to: string };
    pendientes: { from: string; label: string; pendiente: number; sinPagar: string[] }[];
    totales: { calculada: number; registrada: number; pendiente: number }; semanasFuturas: string[];
    pendientePago?: NominaPendiente;   // B4
  };
  gastosSinConcepto: { n: number; total: number; filas: { expense_id: number; fecha: string; importe: number; categoria: string; nota: string; motivo: string }[] };
  pagosSinPeriodo: { n: number; total: number; filas: { payment_id: number; contact_id: number | null; fecha: string; importe: number; alumno: string; metodo: string }[] };
  adelantos: {
    n: number; total: number; cobrado: number; porMes: { mes: string; titulo: string; n: number; total: number }[];
    filas: { payment_id: number; contact_id?: number | null; fecha: string; alumno: string; importe: number; meses: number; periodo: string; parteFutura: number; mesesFuturos: string[]; metodo: string }[];
  };
  alumnas: { resumen: { activas: number; conPlan: number; inscritas: number; sinPlan: number; planSinClase: number; desajuste: number; precioEspecial?: number }; sinPlan: ConcAlumna[]; planSinClase: ConcAlumna[]; desajuste: ConcAlumna[]; precioEspecial?: ConcAlumna[]; nota: string };
  clases: { sinAlumnas: { class_id: number; name: string; profesor: string | null }[]; sinProfesor: number; nota: string };
  avisos: string[]; generado: string;
  puedeMarcarPrecio?: boolean;   // B4: puede marcar «precio especial» (= cambiar planes en la ficha)
}
export function getConciliacion(month: string) { return api.get<Conciliacion>(`/finances/presupuesto/conciliacion?month=${month}`); }
/** B4: marca / quita el plan actual de la alumna como «precio especial a propósito» (deja de contar como plan ≠ clases). */
export function marcarPrecioEspecial(contactId: number, nota?: string) {
  return api.put<{ ok: boolean; accion: string }>('/finances/presupuesto/conciliacion/precio-especial', { contact_id: contactId, nota });
}
export function quitarPrecioEspecial(contactId: number) {
  return api.del<{ ok: boolean; accion: string }>(`/finances/presupuesto/conciliacion/precio-especial?contact_id=${contactId}`);
}

// ── B4 (19-oct-2026): Costo docente por clase (lib/letsmoveCostoDocente.js) ──
export type RepartoSueldo = 'horas' | 'sesiones' | 'administrativo';
export interface CDClase {
  class_id: number; nombre: string; profesores: string; sesiones: number; futuras: number; noDadas: number; sinLista: number; suplencias: number;
  horas: number; porHora: number; sueldoFijo: number; comision: number; total: number; porSesion: number | null; porHoraClase: number | null;
  alumnas: number; porAlumna: number | null; archivada: boolean;
}
export interface CDPersona {
  user_id: number; nombre: string; modo: string; horas: number; sesiones: number; horario: number; sueldoFijo: number; comision: number;
  ajustes: number; bonos: number; calculado: number; calculadoConciliacion: number; cuadra: boolean; sueldoA: 'clases' | 'administrativo' | null;
}
export interface CostoDocente {
  mes: string; titulo: string; regla: { clave: string; texto: string }; parcial: string | null; rango: { from: string; to: string }; hoy: string; enCurso: boolean;
  reparto: RepartoSueldo; repartoDefault: RepartoSueldo; repartos: { clave: RepartoSueldo; texto: string }[];
  clases: CDClase[]; personas: CDPersona[];
  noAsignado: { ajustes: number; bonos: number; sueldoFijoAdministrativo: number; comisionSinClase: number; total: number; detalle: { nombre: string; concepto: string; monto: number }[] };
  totales: { asignado: number; noAsignado: number; nominaDocente: number; cuadre: number; sesiones: number; futuras: number; horas: number; clases: number; porSesion: number | null };
  pendientePago: NominaPendiente; supuestos: string[]; avisos: string[]; generado: string;
}
export function getCostoDocente(month: string, reparto?: RepartoSueldo | null) {
  return api.get<CostoDocente>(`/finances/presupuesto/costo-docente?month=${month}${reparto ? `&reparto=${reparto}` : ''}`);
}
export function urlExportCostoDocente(month: string, reparto?: RepartoSueldo | null) {
  return `/api/letsmove/finances/presupuesto/costo-docente.xlsx?month=${encodeURIComponent(month)}${reparto ? `&reparto=${reparto}` : ''}`;
}

// ── B7 (20-oct-2026): Punto de equilibrio y rentabilidad por clase (lib/letsmoveRentabilidad.js, Mabel #1484) ──
export type RBTipoGasto = 'general' | 'administrativo' | 'pauta' | 'directo' | 'inversion' | 'inventario' | 'nomina' | 'excluido' | 'bono';
export interface RBAlerta { tipo: string; nivel: 'rojo' | 'ambar' | 'azul'; texto: string }
export interface RBClase {
  class_id: number; nombre: string; maestros: string; salonId: number | null; salonNombre: string | null; salon: 'A' | 'B' | null; variosSalones: boolean;
  sesiones: number; sesionesDadas: number; horas: number; alumnas: number; capacidad: number | null; archivada: boolean;
  ingresoReal: number; ingresoProyectado: number; inscripcion: number; alumnasPagan: number; alumnasProyectadas: number;
  // 21-oct-2026 (respuestas de Mabel): clases sueltas dentro del ingreso, parte sabatina de la recepción, último mes de una archivada.
  sueltas?: number; nSueltas?: number; recepcionSabados?: number; ultimoMes?: string | null; dias?: number[];
  docente: { porHora: number; sueldoFijo: number; comision: number; bonos: number; recepcion: number; total: number };
  directos: { pauta: number; material: number; inversion: number; total: number; gastos: { expense_id: number; tipo: RBTipoGasto; concepto: string; monto: number; de: number; clases: number; porConcepto?: boolean }[] };
  generales: number; administrativos: number; costoDirecto: number; costoTotal: number; porSesion: number | null; porAlumna: number | null;
  precioAlumna: number | null; precioEstimado: boolean; peDirecto: number | null; peCompleto: number | null; ingresoMinimo: number; contribucion: number;
  utilidadReal: number; utilidadProyectada: number; margenPct: number | null; ocupacionPct: number | null; alertas: RBAlerta[];
}
export interface RBGasto { expense_id?: number; tipo?: RBTipoGasto; concepto: string; monto: number; motivo?: string; sinClase?: boolean }
export interface RBConfig {
  pctA: number; salones: Record<string, 'A' | 'B' | null> | null; repartoSalon: 'horas' | 'iguales'; repartoAdmin: 'horas' | 'iguales';
  conceptos: Record<string, RBTipoGasto>; asignaciones: Record<string, number[]>; pautaSinClase: 'todas' | 'general' | 'fuera';
  recepcion: { user_id: number; semanal: number; destino: 'administrativo' | 'fuera' | 'clases'; sabados?: { semanal: number; clases: number[] | null } | null } | null;
  bonosAClases: boolean; inscripcion: 'aparte' | 'semestre' | 'no';
  conceptoClases?: Record<string, number[]>; archivadas?: Record<string, string>;
  alertas: { cupoPct: number; margenMinPct: number };
}
export interface Rentabilidad {
  mes: string; titulo: string; clases: RBClase[];
  totales: {
    clases: number; conHorario: number; alumnas: number; ingresoReal: number; ingresoProyectado: number; ingresoSinClase: { real: number; proyectado: number; alumnas: number };
    docente: number; directos: number; generales: number; administrativos: number; costoTotal: number; fuera: number; universo: number; cuadre: number;
    nominaDocente: number; nominaAdmin: number; gastosMes: number; nominaRegistradaGastos: { monto: number; n: number }; generalPool: number; adminPool: number;
    utilidadReal: number; utilidadProyectada: number; precioMedio: number | null; peEstudio: number | null; bajoEquilibrio: number;
    sueltas?: number; sueltasSinClase?: number; inscripcionAparte?: number; recepcionSabados?: number;
    ingresoEstudio?: { real: number; proyectado: number }; utilidadEstudio?: { real: number; proyectado: number }; bonosSinPersona?: { monto: number; n: number };
  };
  excel?: boolean;
  inscripcion?: { modo: 'aparte' | 'semestre' | 'no'; real: number; cobros: number; alumnas: number };
  sueltas?: { total: number; n: number; sinClase: { payment_id: number; fecha: string; monto: number; clase: string | null }[] };
  sabados?: { total: number; clases: { class_id: number; nombre: string; monto: number }[] } | null;
  pautasPorConcepto?: { concept_id: number; concepto: string; porNombre: boolean; clases: { class_id: number; nombre: string }[] }[];
  salones: { salon: 'A' | 'B'; pct: number; monto: number; clases: number; horas: number; salones: string[] }[];
  recepcion: { user_id: number; nombre: string; semanal: number; monto: number; destino: string; nota: string | null;
    sabados?: { semanal: number; monto: number; auto: boolean; clases: { class_id: number; nombre: string }[] } | null; restoASuClase?: number } | null;
  staff: { user_id: number; nombre: string; modo: string; monto: number }[];
  docentes: { user_id: number; nombre: string }[];
  administrativos: { total: number; detalle: RBGasto[] }; generales: { total: number; detalle: RBGasto[] }; fuera: RBGasto[];
  conceptos: { concept_id: number | null; concepto: string; categoria: string; tipo: RBTipoGasto; monto: number; n: number }[];
  config: RBConfig; guardada: Partial<RBConfig>; tipos: { clave: RBTipoGasto; texto: string }[];
  salonesDisponibles: { classroom_id: number; nombre: string; salon: 'A' | 'B' | null; porDefecto: 'A' | 'B' | null }[];
  dudas: { clave: string; texto: string }[]; avisos: string[]; supuestos: string[]; generado: string;
}
export function getRentabilidad(month: string) {
  return api.get<Rentabilidad>(`/finances/presupuesto/rentabilidad?month=${month}`);
}
// «Pasar a Excel» (Mabel #1499, 21-oct-2026): la tabla tal como se ve (mes + filtro de la pantalla). Lo arma el servidor.
export function urlExportRentabilidad(month: string, filtro: string) {
  return `/api/letsmove/finances/presupuesto/rentabilidad.xlsx?month=${encodeURIComponent(month)}&filtro=${encodeURIComponent(filtro)}`;
}

// ── Estado de resultados (B1, 15-oct-2026): sub-pestaña de Pronóstico y presupuesto ──
// Lo arma el servidor en lib/letsmoveResultados.js reordenando «Por apartado» (cuadra con él por construcción).
export interface ERHijo { nombre: string; valores: Record<string, number>; acumulado: number }
export interface ERFila {
  k: string; label: string; tipo: 'linea' | 'subtotal' | 'resultado'; signo?: number; nota?: string; margen?: string;
  valores: Record<string, number>; acumulado: number; margenes: Record<string, number | null> | null; margenAcumulado: number | null; hijos: ERHijo[];
}
export interface ERColumna { mes: string; titulo: string; corto: string; parcial: boolean; enCurso: boolean; sinNominaRegistrada: boolean }
export interface ERMes {
  mes: string; titulo: string; parcial: string | null; enCurso: boolean; sinNominaRegistrada: boolean;
  nomina: { registrada: number; calculada: number; calculadaDocente: number; calculadaAdministrativa: number; sinRegistrar: number };
  conNominaCalculada: { operativo: number; neto: number; margenOperativo: number | null; margenNeto: number | null };
  porApartado: { ingresos: number; egresos: number; nomina: number; inversiones: number; resultado: number; resultadoNominaCalculada: number };
}
export interface EstadoResultados {
  mes: string; titulo: string; anio: number; desde: string; rotuloAcumulado: string; regla: { clave: string; texto: string };
  columnas: ERColumna[]; filas: ERFila[]; meses: ERMes[];
  acumulado: Record<string, any> & { conNominaCalculada: { operativo: number; neto: number; margenOperativo: number | null; margenNeto: number | null }; nominaSinRegistrar: number };
  supuestos: string[]; avisos: string[]; generado: string;
}
// `desde` (trimestre/semestre, 24-oct-2026): columnas desde ese mes y el acumulado = total del periodo.
const qDesde = (desde?: string | null) => (desde ? `&desde=${encodeURIComponent(desde)}` : '');
export function getEstadoResultados(month: string, desde?: string | null) { return api.get<EstadoResultados>(`/finances/presupuesto/resultados?month=${month}${qDesde(desde)}`); }
export function urlExportResultados(month: string, desde?: string | null) { return `/api/letsmove/finances/presupuesto/resultados.xlsx?month=${encodeURIComponent(month)}${qDesde(desde)}`; }

// ── Trimestre / semestre (Mabel 23-oct-2026): suma de los reportes mensuales del periodo (lib/letsmovePeriodo.js) ──
export type TipoPeriodo = 'trimestre' | 'semestre';
export interface PeriodoInfo { tipo: TipoPeriodo; desde: string; hasta: string; meses: string[]; titulo: string; tramo: string; anio: number; movil?: boolean; anterior: string; siguiente: string }
export interface PeriodoColumna { mes: string; titulo: string; corto: string; estado: 'cerrado' | 'en_curso' | 'pronostico'; pronostico?: boolean }
export interface PeriodoResumenFila { k: string; label: string; resultado: boolean; valores: Record<string, number | null>; total: number; totalReal: number }
export interface PeriodoIngresoFila {
  k: string; categoria: string; concepto: string; porMes: Record<string, { presupuesto: number | null; real: number | null; n: number }>;
  presupuesto: number | null; real: number; n: number; variacion: number | null; pct: number | null;
}
export interface PeriodoValores { valores: Record<string, number>; total: number }
export interface PresupuestoPeriodo {
  periodo: PeriodoInfo; titulo: string; columnas: PeriodoColumna[]; resumen: PeriodoResumenFila[]; ingresos: PeriodoIngresoFila[];
  estructura: { secciones: (PeriodoValores & { section: string; label: string; categorias: (PeriodoValores & { category_id: number; name: string; conceptos: (PeriodoValores & { concept_id: number; name: string })[] })[] })[]; resultado: PeriodoValores } | null;
  presupuestoGastos: { categorias: { apartado: string; categoria: string; presupuesto: number; real: number; diferencia: number }[]; totales: { presupuesto: number; real: number; diferencia: number }; nota: string } | null;
  notas: string[]; hayFuturos: boolean; generado: string;
}
// `movil` (24-oct-2026): ventana que empieza en `month` (feb–abr…) en vez del periodo de calendario.
const qMovil = (movil?: boolean) => (movil ? '&movil=1' : '');
export function getPresupuestoPeriodo(tipo: TipoPeriodo, month: string, movil?: boolean) {
  return api.get<PresupuestoPeriodo>(`/finances/presupuesto/periodo?tipo=${tipo}&month=${month}${qMovil(movil)}`);
}
export function urlExportPresupuestoPeriodo(tipo: TipoPeriodo, month: string, movil?: boolean) {
  return `/api/letsmove/finances/presupuesto/periodo.xlsx?tipo=${tipo}&month=${encodeURIComponent(month)}${qMovil(movil)}`;
}
export interface RentabilidadPeriodoClase {
  class_id: number; nombre: string; maestros: string | null; salon: string | null; alumnas: number; archivada: boolean; meses: string[];
  sesiones: number; horas: number; ingresoReal: number; docente: number; directos: number; generales: number; administrativos: number;
  costoTotal: number; utilidadReal: number; margenPct: number | null;
}
export interface RentabilidadPeriodo {
  periodo: PeriodoInfo; titulo: string; meses: string[]; columnas: PeriodoColumna[]; clases: RentabilidadPeriodoClase[];
  totales: { clases: number; ingresoReal: number; ingresoEstudioReal: number; ingresoSinClase: number; docente: number; directos: number; generales: number;
    administrativos: number; costoTotal: number; utilidadReal: number; utilidadEstudioReal: number; margenPct: number | null };
  porMes: Record<string, { ingresoEstudio: number; costoTotal: number; utilidadEstudio: number }>;
  notas: string[]; generado: string;
}
export function getRentabilidadPeriodo(tipo: TipoPeriodo, month: string, movil?: boolean) {
  return api.get<RentabilidadPeriodo>(`/finances/presupuesto/rentabilidad-periodo?tipo=${tipo}&month=${month}${qMovil(movil)}`);
}
export function urlExportRentabilidadPeriodo(tipo: TipoPeriodo, month: string, movil?: boolean) {
  return `/api/letsmove/finances/presupuesto/rentabilidad-periodo.xlsx?tipo=${tipo}&month=${encodeURIComponent(month)}${qMovil(movil)}`;
}

// ── B3 (16-oct-2026): Cierre estimado del mes + mes siguiente, con 2 perillas (lib/letsmoveCierre.js) ──
export type Escenarios = { conservador: number; esperado: number; optimista: number };
export interface CierreLinea { k: string; concepto: string; signo: 1 | -1; valores: Escenarios; explica: string; referencia?: boolean }
export interface CierreOtro { k: string; concepto: string; referencia: number | null; cobrado?: number; falta?: number }
export interface CierreCargo { debt_id: number; tipo: string; nombre: string; activa: boolean; vence: string | null; monto: number; abonos: number; saldo: number }
export interface CierrePresConcepto { categoria: string; concepto: string; calculado: boolean; presupuesto: number; real: number; faltaPorPagar: number }
export interface CierrePres { disponible: boolean; aviso: string | null; presupuesto: number; real: number; faltaPorPagar: number; nominaExcluida: { presupuesto: number; real: number; faltaPorPagar: number }; conceptos: CierrePresConcepto[] }
export interface CierreBloque {
  mes: string; titulo: string; lineas: CierreLinea[]; resultado: Escenarios;
  evento: { referencia: number | null; cobrado?: number; explica: string };
  detalle: {
    cargos?: { lista: CierreCargo[]; activas: { n: number; saldo: number }; bajas: { n: number; saldo: number } };
    otros: CierreOtro[]; anioAnterior: { mes: string; titulo: string; hay: boolean };
    presupuesto: CierrePres;
    nomina: { total?: number; horario?: number; semanas: { label: string; calculada: number; registrada?: number; falta?: number; personas?: number }[]; diferenciasPagadas?: { n: number; total: number }; comisionCalculadaHoy?: number };
    alumnas?: number; activasPorPlan?: number; yaCobrado?: number; pagosAdelantados?: number; resto?: number;
  };
}
export interface Cierre {
  mes: string; titulo: string; hoy: string; enCurso: boolean; pasado: boolean; futuro: boolean; aviso: string | null;
  supuestos: {
    bajas: number; cobranza: number; bajasMedido: number; cobranzaMedido: number; editado: { bajas: boolean; cobranza: boolean };
    escenarios: Record<'conservador' | 'esperado' | 'optimista', { bajas: number; cobranza: number }>;
    medicion: {
      bajas: { desde: string; hasta: string; pagaron: number; noSiguieron: number; pct: number | null; titulo: string; explica: string; anioAnterior: { pct: number | null; titulo: string } };
      cobranza: { aTiempoPct: number | null; activas: { n: number; monto: number; cobradoN: number; cobrado: number; pct: number | null }; bajas: { n: number; monto: number; cobradoN: number; cobrado: number; pct: number | null }; explica: string; bajasExplica: string; aTiempoExplica: string };
    };
    explica: string; notaBajasMesEnCurso: string;
  };
  cierre: CierreBloque | null; siguiente: CierreBloque | null;
  regla: { clave: string; texto: string }; notas: string[]; generado: string;
}
const qsPerillas = (p?: { bajas?: number | null; cobranza?: number | null }) =>
  (p?.bajas != null ? `&bajas=${p.bajas}` : '') + (p?.cobranza != null ? `&cobranza=${p.cobranza}` : '');
export function getCierre(month: string, p?: { bajas?: number | null; cobranza?: number | null }) {
  return api.get<Cierre>(`/finances/presupuesto/cierre?month=${month}${qsPerillas(p)}`);
}
/** PDF/Excel de Pronóstico y presupuesto con la hoja «Cierre estimado» calculada con estas perillas. */
export function urlExportCierre(month: string, fmt: 'xlsx' | 'pdf', p?: { bajas?: number | null; cobranza?: number | null }) {
  return `/api/letsmove/finances/presupuesto.${fmt}?month=${encodeURIComponent(month)}${qsPerillas(p)}`;
}
